import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'
import { ref } from 'vue'
import accountStore from '@/stores/account.store'
import amplitudeUserIdHash from '@/services/amplitude/amplitude-user-id-hash.service'

class AmplitudeExperimentService {
  private client: ExperimentClient | null = null

  /**
   * Reactive readiness signal. Flips to `true` once the experiment state is
   * settled — variants fetched, or we know none are coming (no deployment key,
   * consent revoked, or init failed).
   *
   * Consumers read this (directly or via {@link variant}) so their computeds
   * re-evaluate once assignments arrive. This avoids the race where the UI is
   * built against the fallback (control) simply because the fetch had not yet
   * returned — the Experiment fetch runs behind the consent flow and finishes
   * seconds after the first onboarding / mainscreen evaluation.
   */
  private readonly ready = ref(false)

  private resolveReady!: () => void
  private readonly readyPromise = new Promise<void>((resolve) => {
    this.resolveReady = resolve
  })

  private markReady(): void {
    if (this.ready.value) return
    this.ready.value = true
    this.resolveReady()
  }

  /**
   * Resolves once the experiment state is settled, or after `timeoutMs` as a
   * fail-safe so callers never hang (they then fall back to control).
   */
  public whenReady(timeoutMs = 4000): Promise<void> {
    if (this.ready.value) return Promise.resolve()
    return Promise.race([
      this.readyPromise,
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ])
  }

  /**
   * Initializes the Experiment client and fetches flag variants for the user.
   * Must be called after Amplitude Analytics is initialized and the user ID is set.
   * Uses `initializeWithAmplitudeAnalytics` which automatically wires up exposure
   * tracking — this is what creates the [Experiment] <flag_key> user property in Amplitude.
   *
   * Variant fetch uses the hashed pseudonymous user ID when the consent proxy is
   * configured (OCPT-816), so [Experiment] Assignment lands on the same Amplitude
   * profile as analytics events routed through the proxy.
   */
  public async initialize(): Promise<void> {
    const deploymentKey = import.meta.env
      .VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY

    if (!deploymentKey) {
      console.warn('AmplitudeExperimentService: No deployment key configured')
      this.markReady()
      return
    }

    try {
      this.client = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
        serverZone: 'EU',
      })

      const rawUserId = accountStore.state.account?.uuid
      const experimentUser = rawUserId
        ? { user_id: await amplitudeUserIdHash.getHashedUserId(rawUserId) }
        : undefined

      await this.client.fetch(experimentUser)
    } catch (e) {
      console.warn('AmplitudeExperimentService: Initialization failed', e)
    } finally {
      this.markReady()
    }
  }

  /**
   * Tears down the Experiment client and clears cached variants.
   * Should be called when the user revokes consent.
   */
  public teardown(): void {
    this.client = null
    this.markReady()
  }

  /**
   * Returns the assigned variant value for a given flag key.
   * Returns null if the client is not initialized or no variant is assigned.
   */
  public variant(flagKey: string): string | null {
    // Reading `ready` registers a reactive dependency so computed consumers
    // re-evaluate once variants are fetched. Until settled we return null so
    // callers use the control fallback rather than a not-yet-fetched value.
    if (!this.ready.value) return null
    return this.client?.variant(flagKey)?.value ?? null
  }
}

export default new AmplitudeExperimentService()
